
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface ProductOption {
  name: string;
  image: string;
}

interface ProductCategory {
  title: string;
  options: ProductOption[];
  align: 'left' | 'right';
}

function CategoryBlock({ category, index }: { category: ProductCategory; index: number }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const isLeft = category.align === 'left';
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.unobserve(el); } },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${index * 150}ms` }}
      className={`flex flex-col md:flex-row items-center gap-5 md:gap-12 transition-all duration-700
        ${!isLeft ? 'md:flex-row-reverse' : ''}
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
    >
      {/* Menu Side */}
      <div className="w-full md:w-1/2 flex flex-col items-start">
        <h3 className="text-xl sm:text-3xl md:text-4xl font-bold text-white mb-4 md:mb-8 tracking-tight">
          {category.title}
        </h3>
        <div className="flex flex-col gap-1.5 sm:gap-2 w-full">
          {category.options.map((option, idx) => {
            const isActive = idx === activeIndex;
            return (
              <button
                key={idx}
                onClick={() => setActiveIndex(idx)}
                onMouseEnter={() => setActiveIndex(idx)}
                className={`
                  text-left px-4 py-3 sm:px-5 sm:py-4 rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap
                  flex items-center gap-2.5 sm:gap-3 group
                  ${isActive
                    ? 'bg-white/20 backdrop-blur-sm text-white shadow-lg shadow-black/10'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'}
                `}
              >
                <span
                  className={`
                    w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-all duration-300 flex-shrink-0
                    ${isActive ? 'bg-[#FFD700] scale-125' : 'bg-white/30 group-hover:bg-white/50'}
                  `}
                />
                <span
                  className={`
                    text-sm sm:text-lg md:text-xl font-medium transition-all duration-300
                    ${isActive ? 'translate-x-1' : ''}
                  `}
                >
                  {option.name}
                </span>
                {isActive && (
                  <span className="w-5 h-5 flex items-center justify-center ml-auto">
                    <i className="ri-arrow-right-s-line text-[#FFD700] text-lg sm:text-xl" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Image Side */}
      <div className="w-full md:w-1/2">
        <div className="relative w-full h-56 sm:h-80 md:h-[420px] rounded-xl overflow-hidden shadow-2xl shadow-black/20">
          {category.options.map((option, idx) => (
            <img
              key={idx}
              src={option.image}
              alt={option.name}
              className={`
                absolute inset-0 w-full h-full object-cover object-top transition-all duration-500
                ${idx === activeIndex ? 'opacity-100 scale-100' : 'opacity-0 scale-105'}
              `}
            />
          ))}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3 sm:p-6">
            <p className="text-white font-bold text-base sm:text-xl">
              {category.options[activeIndex].name}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProductGallery() {
  const { t } = useTranslation();

  const productCategories = [
    {
      title: 'Palmito',
      titleEn: 'Heart of Palm',
      options: [
        {
          name: 'Palmito Triturado',
          nameEn: 'Shredded Heart of Palm',
          image: 'https://public.readdy.ai/ai/img_res/50058906c8715b17bbcf61f0ed4c6947.jpg'
        },
        {
          name: 'Palmito Arroz',
          nameEn: 'Heart of Palm Rice',
          image: 'https://readdy.ai/api/search-image?query=heart%20of%20palm%20rice%20grains%20texture%20in%20premium%20glass%20container%20on%20clean%20white%20marble%20surface%20soft%20natural%20lighting%20professional%20food%20photography%20minimal%20background%20costa%20rican%20export%20quality&width=600&height=600&seq=33&orientation=squarish'
        },
        {
          name: 'Pasta Cabello de Ángel de Palmito',
          nameEn: 'Heart of Palm Angel Hair Pasta',
          image: 'https://readdy.ai/api/search-image?query=heart%20of%20palm%20angel%20hair%20pasta%20thin%20noodles%20in%20elegant%20glass%20jar%20on%20clean%20white%20marble%20surface%20soft%20natural%20lighting%20professional%20product%20photography%20minimal%20background%20premium%20costa%20rican%20food&width=600&height=600&seq=34&orientation=squarish'
        },
        {
          name: 'Palmito Entero',
          nameEn: 'Whole Heart of Palm',
          image: 'https://readdy.ai/api/search-image?query=whole%20heart%20of%20palm%20cylinders%20in%20premium%20glass%20jar%20on%20clean%20white%20marble%20surface%20soft%20natural%20lighting%20professional%20product%20photography%20minimal%20background%20costa%20rican%20agricultural%20export&width=600&height=600&seq=35&orientation=squarish'
        },
        {
          name: 'Palmito Doble',
          nameEn: 'Double Heart of Palm',
          image: 'https://readdy.ai/api/search-image?query=double%20heart%20of%20palm%20premium%20cuts%20in%20elegant%20glass%20container%20on%20clean%20white%20marble%20surface%20soft%20natural%20lighting%20professional%20food%20photography%20minimal%20background%20costa%20rican%20quality&width=600&height=600&seq=36&orientation=squarish'
        },
        {
          name: 'Palmito en Lasaña',
          nameEn: 'Heart of Palm Lasagna Sheets',
          image: 'https://readdy.ai/api/search-image?query=heart%20of%20palm%20lasagna%20sheets%20flat%20strips%20in%20premium%20glass%20jar%20on%20clean%20white%20marble%20surface%20soft%20natural%20lighting%20professional%20product%20photography%20minimal%20background%20costa%20rican%20gourmet&width=600&height=600&seq=37&orientation=squarish'
        },
        {
          name: 'Palmito en trozos',
          nameEn: 'Heart of Palm Chunks',
          image: 'https://static.readdy.ai/image/d4364c93ef6f707650170405d0b37024/9a25dba4ccc402840af6fdfa4823aeaf.png'
        }
      ]
    },
    {
      title: 'Piña',
      titleEn: 'Pineapple',
      options: [
        {
          name: 'Piña Triturada',
          nameEn: 'Shredded Pineapple',
          image: 'https://static.readdy.ai/image/d4364c93ef6f707650170405d0b37024/45830f7e46d8a3d8c6c25b791321d649.png'
        },
        {
          name: 'Piña en Trozos',
          nameEn: 'Pineapple Chunks',
          image: 'https://static.readdy.ai/image/d4364c93ef6f707650170405d0b37024/d9cfbfb23e13462166ce9b351e862a98.png'
        },
        {
          name: 'Piña en Rodajas',
          nameEn: 'Pineapple Slices',
          image: 'https://static.readdy.ai/image/d4364c93ef6f707650170405d0b37024/1a75e7fea1f78c93d2c4cbb5f55d35e0.png'
        }
      ]
    }
  ];

  const categories: ProductCategory[] = [
    {
      title: t('products.cat1.title'), // Palmito
      align: 'left',
      options: [
        {
          name: t('products.cat1.opt1'), // Palmito Triturado
          image: 'https://static.readdy.ai/image/d4364c93ef6f707650170405d0b37024/c73b71873636e09741325ad54f0bffdb.png',
        },
        {
          name: t('products.cat1.opt2'), // Palmito Arroz
          image: 'https://static.readdy.ai/image/d4364c93ef6f707650170405d0b37024/17b81d763b4a0b4a127fc769240c2fd1.png',
        },
        {
          name: t('products.cat1.opt3'), // Pasta Cabello de Ángel de Palmito
          image: 'https://static.readdy.ai/image/d4364c93ef6f707650170405d0b37024/8b3d4834b304e2fb801c6383696cd6c4.png',
        },
        {
          name: t('products.cat3.opt1'), // Palmito Entero
          image: 'https://static.readdy.ai/image/d4364c93ef6f707650170405d0b37024/47493ac07af666e2c0126580e4a41c42.png',
        },
        {
          name: t('products.cat3.opt2'), // Palmito Doble
          image: 'https://static.readdy.ai/image/d4364c93ef6f707650170405d0b37024/29be408f1852f3b92cee4973497a3609.png',
        },
        {
          name: t('products.cat3.opt3'), // Palmito en Lasaña
          image: 'https://static.readdy.ai/image/d4364c93ef6f707650170405d0b37024/b781bcfff55d489308f3dd727fcae3f7.png',
        },
        {
          name: t('products.cat3.opt4'), // Palmito en trozos
          image: 'https://static.readdy.ai/image/d4364c93ef6f707650170405d0b37024/9a25dba4ccc402840af6fdfa4823aeaf.png',
        },
      ],
    },
    {
      title: t('products.cat2.title'), // Piña
      align: 'right',
      options: [
        {
          name: t('products.cat2.opt1'), // Piña Triturada
          image: 'https://static.readdy.ai/image/d4364c93ef6f707650170405d0b37024/45830f7e46d8a3d8c6c25b791321d649.png',
        },
        {
          name: t('products.cat2.opt2'), // Piña en Trozos
          image: 'https://static.readdy.ai/image/d4364c93ef6f707650170405d0b37024/d9cfbfb23e13462166ce9b351e862a98.png',
        },
        {
          name: t('products.cat2.opt3'), // Piña en Rodajas
          image: 'https://static.readdy.ai/image/d4364c93ef6f707650170405d0b37024/1a75e7fea1f78c93d2c4cbb5f55d35e0.png',
        },
      ],
    },
  ];

  return (
    <section id="productos" className="py-12 sm:py-24 relative overflow-hidden">
      {/* Imagen de fondo con ondas */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url('https://static.readdy.ai/image/d4364c93ef6f707650170405d0b37024/4ca710f43f023379c338a084368042d1.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
        aria-hidden="true"
      />
      {/* Overlay sutil para mantener legibilidad */}
      <div className="absolute inset-0 bg-black/15" aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 relative z-10">
        <div className="text-center mb-12 sm:mb-24">
          <p className="text-xs sm:text-sm font-semibold text-[#FFD700] uppercase tracking-widest mb-2 sm:mb-3">
            {t('products.tag')}
          </p>
          <h2 className="text-[1.4rem] leading-tight sm:text-4xl md:text-5xl font-bold text-white mb-2 sm:mb-4">
            {t('products.title')}
          </h2>
          <p className="mt-2 sm:mt-3 text-sm sm:text-lg text-white/90 max-w-3xl mx-auto flex items-center justify-center gap-2">
            <span className="text-[#FFD700] font-bold flex-shrink-0">✦✦</span>
            <span>{t('products.note')}</span>
          </p>
        </div>

        <div className="flex flex-col gap-16 sm:gap-32" data-product-shop>
          {categories.map((category, index) => (
            <CategoryBlock key={index} category={category} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
